import { Message, QueryFilter } from '@dinosband/bv-messenger-schema'
import { queryMessageDetail, queryMessages, queryMessagesByIds } from '@dinosband/bv-messenger-client'
import { _first, _last, _sortBy } from '@dinosband/dbi-utils'

import { apolloClient, dataCenter, messengerStore } from '../../'
import { getCacheChunk, getFilteredCacheChunk } from '../filter/message-from-cache'
import { cacheBulkPut, MessagesChunkSize } from '..'


export async function getMessages (chatRoomId: number, direction: 'before' | 'after' | 'around' | 'recent',
                                   message?: { id?: number, seq?: number}, useCache?: boolean, filter?: QueryFilter) {
  const cache = (await import('@dinosband/bv-cache'))?.messageCache
  const range = await cache.getRange(chatRoomId)
  const size_ = MessagesChunkSize
  const id = message?.id, seq = message?.seq
  if (useCache && ((id && seq) || (!id && !seq))) {
    if (direction === 'recent') {
      return getRecentMessages(chatRoomId, size_, message, filter)
    } else if (direction === 'after' && id && seq) {
      return getAfterMessages(chatRoomId, size_, { id, seq }, filter)
    } else if (direction === 'around' && id && seq) {
      return getAroundMessages(chatRoomId, size_, seq, id, filter)
    }
  }
  const messages = await queryMessages(await apolloClient.getClient(), [chatRoomId], size_, direction, id, range, filter)
  if (!messages) {
    return []
  }
  const result = await getMessagesWithCache(chatRoomId, messages)
  return result
}

export async function getRootMessage (chatRoomId: number, messageId: number) {
  const rootMessageCache = (await import('@dinosband/bv-cache'))?.rootMessageCache
  if (!rootMessageCache.isLoaded) {
    await rootMessageCache.load()
  }
  let message = rootMessageCache.getById(messageId)
  if (message) {
    return message
  }
  message = await queryMessageDetail(await apolloClient.getClient(), chatRoomId, messageId) ?? null
  if (message) {
    rootMessageCache.put(message)
  } else {
    console.error('getMessage: Failed to get message id =', messageId)
  }
  return message
}

export async function getMessageById (chatRoomId: number | undefined, messageId: number) {
  if (chatRoomId) {
    const cache = (await import('@dinosband/bv-cache'))?.messageCache
    const message = await cache.getById(chatRoomId, messageId)
    if (message) {
      return message
    }
  }
  const result = await getMessagesByIds([messageId])
  return _first(result)
}

export async function getMessagesByIds (messageIds: number[]) {
  const result = await queryMessagesByIds(await apolloClient.getClient(), messageIds)
  if (result) {
    cacheBulkPut(undefined, result)
  }
  return result
}

async function getMessagesWithCache (chatRoomId: number, result: Message[]) {
  const idsNotInCache: number[] = []
  const updateds: Message[] = []
  let messages: Message[] = []
  for (const mesg of result) {
    if (mesg.props) {
      updateds.push(mesg)
      messages.push(mesg)
    } else {
      const cache = (await import('@dinosband/bv-cache'))?.messageCache
      const cached = await cache.get(mesg.chatRoomId, mesg)
      if (cached) {
        messages.push(cached)
      } else {
        idsNotInCache.push(mesg.id)
      }
    }
  }

  // stage 3: get message bodies of ids not in cache from server
  if (idsNotInCache.length > 0) {
    const newMessages = await queryMessagesByIds(await apolloClient.getClient(), idsNotInCache)
    if (newMessages) {
      updateds.push(...newMessages)
      messages = _sortBy(messages.concat(newMessages), (obj) => obj.id)
    }
  }
  cacheBulkPut(chatRoomId, updateds)
  return messages
}

async function getRecentMessages (chatRoomId: number, size: number,
                                  message?: { seq?: number, id?: number}, filter?: QueryFilter) {
  const lastMessage_ = messengerStore.chatRoomContext(chatRoomId).lastMessage ?? lastMessage(chatRoomId)
  if (!lastMessage_) {
    return await queryMessages_(chatRoomId, size, 'recent', message?.id, filter)
  }
  if (lastMessage_.seq == message?.seq) {
    return []
  }
  const seq = Math.max((message?.seq ?? 0) + 1, lastMessage_.seq - size + 1)
  const excludeDeleted = filter?.excludeDeleted
  const cacheds = filter ?
    await getFilteredCacheChunk(chatRoomId, 'recent', lastMessage_.seq, size, excludeDeleted) :
    await getCacheChunk(chatRoomId, seq, size, excludeDeleted)
  if (cacheds.length == 0) {
    return await queryMessages_(chatRoomId, size, 'recent', message?.id, filter)
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const last = _last(cacheds)!
  if (last.seq === lastMessage_.seq) {
    return cacheds
  }
  const recents = await queryMessages_(chatRoomId, size, 'recent', last.id, filter) ?? []
  if (size <= recents.length) {
    return recents // is enough
  }
  return [...cacheds, ...recents]
}

async function getAfterMessages (chatRoomId: number, size: number,
                                 message: { seq: number, id: number}, filter?: QueryFilter) {
  const lastMessage_ = lastMessage(chatRoomId)
  if (!lastMessage_ || lastMessage_.seq == message.seq) {
    return []
  }
  const excludeDeleted = filter?.excludeDeleted
  const cacheds = filter ?
    await getFilteredCacheChunk(chatRoomId, 'after', message.seq, size + 1, excludeDeleted) :
    await getCacheChunk(chatRoomId, message.seq + 1, size, excludeDeleted)
  if (cacheds.length == 0) {
    return await queryMessages_(chatRoomId, size, 'after', message.id, filter)
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const last = _last(cacheds)!
  if (last.seq === lastMessage_.seq) {
    return cacheds
  }
  const recents = await queryMessages_(chatRoomId, size, 'after', last.id, filter) ?? []
  return [...cacheds, ...recents]
}

async function getAroundMessages (chatRoomId: number, size: number,
                                  seq: number, id: number, filter?: QueryFilter) {
  const range = _aroundRange(seq)
  const excludeDeleted = filter?.excludeDeleted
  const cacheds = filter ?
    await getFilteredCacheChunk(chatRoomId, 'around', seq, size, excludeDeleted) :
    await getCacheChunk(chatRoomId, range[0], size, excludeDeleted)
  if (cacheds.length == 0) {
    return await queryMessages_(chatRoomId, size, 'around', id, filter)
  }
  // console.error('getAroundMessages', chatRoomId, seq, id, size)
  const remained = size - cacheds.length
  const lastMessage_ = lastMessage(chatRoomId)
  if (lastMessage_?.seq === _last(cacheds)?.seq || remained <= 0) {
    return cacheds
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const last = _last(cacheds)!
  const recents = await queryMessages_(chatRoomId, remained, 'after', last.id, filter) ?? []
  return [...cacheds, ...recents]
}

function _aroundRange (seq: number, size?: number) {
  size = size ?? MessagesChunkSize
  const min = Math.max(1, seq - Math.floor(size / 2))
  const max = seq + Math.floor(size / 2) + 1
  return [min, max]
}

async function queryMessages_ (chatRoomId: number, size: number,
                               direction: 'before' | 'after' | 'around' | 'recent', messageId?: number,
                               filter?: QueryFilter) {
  const result = await queryMessages(await apolloClient.getClient(), [chatRoomId], size, direction, messageId, undefined, filter)
  if (result) {
    cacheBulkPut(chatRoomId, result)
  }
  return result
}

function lastMessage (chatRoomId: number) {
  return dataCenter.messages.lastMessage(chatRoomId)
}
